import type { StatusConfig } from '@/components/ui';

/**
 * EAA-1815: small display-only helpers shared by the Organizations screens
 * (list, detail, create). No data lives here — everything is a pure function
 * over values the caller already fetched.
 */

/**
 * Shared status→config map for the org "Trạng thái" pill — both the list
 * table and the detail header render the exact same base states, so this
 * lives once here rather than being redeclared per page (StatusTag itself
 * stays domain-free; this is the one org-shaped vocabulary both screens share).
 * Colors: success = frame's #d1fae5/#047857, warning = #fffbeb/#b45309 —
 * both already the tokens StatusTag's own tone map resolves to.
 *
 * EAA-1818: `no_unit` added alongside `no_tenant`, not instead of it. The
 * list page's row pill (`OrganizationsTable.tsx`) now measures child-org
 * ("đơn vị") emptiness, while `OrgDetailHeader.tsx`'s pill still legitimately
 * measures that single org's own tenant emptiness — two different backend
 * entities (`Organization.parent` vs `OrganizationAppScope`) that happen to
 * share this generic "active/inactive/empty" pill shape. Renaming
 * `no_tenant` away would have silently mislabelled the detail page's pill,
 * which this ticket does not own and does not intend to change.
 */
// EAA-1845: was a module-level constant with hardcoded Vietnamese labels.
// This is a plain (non-component) util file, so it cannot call the
// `useTranslation` hook itself — turned into a factory the two consuming
// components (`OrganizationsTable.tsx`, `OrgDetailHeader.tsx`) call with
// their own `t`, so the labels stay translatable without giving this file a
// hook it can't have.
export function getOrgStatusConfig(t: (key: string) => string): Record<string, StatusConfig> {
    return {
        active: { label: t('admin.organizations.status.active'), tone: 'success' },
        inactive: { label: t('admin.organizations.status.inactive'), tone: 'default' },
        no_tenant: { label: t('admin.organizations.status.noTenant'), tone: 'warning' },
        no_unit: { label: t('admin.organizations.status.noUnit'), tone: 'warning' },
    };
}

/**
 * Two-letter avatar initials. Same rule `ActingOrgPicker.tsx` already uses
 * for the acting-org tile (first letter of the first two words) — kept
 * consistent rather than inventing a second convention, since the design
 * frames' example initials ("EH", "TA"…) are hand-picked illustrative copy,
 * not the output of a documented algorithm.
 */
export function orgInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

/** `^[a-z0-9-]+$` — the org code pattern the backend enforces (slug, immutable). */
export const ORG_CODE_PATTERN = /^[a-z0-9-]+$/;

export function slugifyHint(name: string): string {
    return name
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function formatDateVN(iso: string | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('vi-VN');
}

export function formatCountVN(value: number | undefined): string {
    if (value === undefined) return '—';
    return value.toLocaleString('vi-VN');
}

/** Org health, in priority order — mirrors the "Trạng thái" column (frame 21)
 * and the detail header pill (frame 22). Inactive always wins over "empty":
 * an org an admin turned off is not merely "missing setup".
 *
 * EAA-1818: `emptyState` picks WHICH "empty" this call means — `'no_tenant'`
 * (default, unchanged) for callers measuring an org's own tenant count
 * (`OrgDetailHeader.tsx`), `'no_unit'` for the list page's child-org count
 * (`OrganizationsTable.tsx`). The default preserves every existing caller's
 * behaviour untouched; only the list table passes the third argument.
 */
export type OrgHealth = 'active' | 'inactive' | 'no_tenant' | 'no_unit';

export function orgHealth(
    isActive: boolean,
    emptyCount: number | undefined,
    emptyState: 'no_tenant' | 'no_unit' = 'no_tenant'
): OrgHealth {
    if (!isActive) return 'inactive';
    if (emptyCount === 0) return emptyState;
    return 'active';
}
